import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack} from 'views/components';
import {DeleteConfirmDialog} from 'views/module';

import backend from 'utils/backend';

import SkillsList from './skill_view/SkillsList';
import SkillsView from './skill_view/SkillsView';
import SkillsNew from './skills_new/SkillsNew';

const SkillsScene = (props) => {
  const {skills, skillsAdd, skillsRemove, statusProgressWrap: progressWrap} = props;

  const [activeNew, setActiveNew] = React.useState(false);
  const [editingSkill, setEditingSkill] = React.useState(null);
  const [isShowingConfirmDeleteDialog, setIsShowingConfirmDeleteDialog] = React.useState(false);
  const [selectedSkillRn, setSelectedSkillRn] = React.useState(null);

  const {groupOptions, groupedSkills} = React.useMemo(() => {
    const uniqueGroups = new Set();
    const groupedSkillsMap = new Map();

    skills.forEach((skill) => {
      if (skill.groupName) {
        uniqueGroups.add(skill.groupName);
      }

      const groupName = skill.groupName || 'Ungrouped';
      const existingGroup = groupedSkillsMap.get(groupName);

      if (existingGroup) {
        existingGroup.push(skill);
        return;
      }

      groupedSkillsMap.set(groupName, [skill]);
    });

    return {
      groupOptions: Array.from(uniqueGroups),
      groupedSkills: Array.from(groupedSkillsMap, ([groupName, groupedGroupSkills]) => ({
        groupName,
        skills: groupedGroupSkills,
      })),
    };
  }, [skills]);

  const selectedSkill = React.useMemo( () => {
    return skills.find((skill) => skill.skillRn === selectedSkillRn) || null;
  }, [selectedSkillRn, skills]);

  const openNew = React.useCallback(() => {
    setEditingSkill(null);
    setActiveNew(true);
  }, []);

  const closeNew = React.useCallback(() => {
    setActiveNew(false);
    setEditingSkill(null);
  }, []);

  const handleSelectSkill = React.useCallback((skill) => {
    setSelectedSkillRn(skill.skillRn);
  }, []);

  const openDeleteConfirm = React.useCallback(() => {
    setIsShowingConfirmDeleteDialog(true);
  }, []);

  const handleEditSkill = React.useCallback(() => {
    setEditingSkill(selectedSkill);
    setActiveNew(true);
  }, [selectedSkill]);

  const handleCreateSkill = React.useCallback(async (skillPayload, replacedSkillRn) => {
    await progressWrap(async () => {
      const createdSkill = await backend.skills.createSkill(skillPayload);

      skillsAdd(createdSkill.skill);
      setSelectedSkillRn(createdSkill.skillRn);

      if (replacedSkillRn) {
        await backend.skills.deleteSkill(replacedSkillRn);
        skillsRemove(replacedSkillRn);
      }

      closeNew();
    });
  }, [closeNew, progressWrap, skillsAdd, skillsRemove]);

  const handleDeleteSkill = React.useCallback(() => {
    if (!selectedSkill) {
      return;
    }

    const skillRn = selectedSkill.skillRn;
    setIsShowingConfirmDeleteDialog(false);

    progressWrap(async () => {
      await backend.skills.deleteSkill(skillRn);
      skillsRemove(skillRn);
      setSelectedSkillRn(null);
    });
  }, [progressWrap, selectedSkill, skillsRemove]);

  React.useEffect(() => {
    if (selectedSkillRn === null) {
      return;
    }

    const hasSelectedSkill = skills.some((skill) => skill.skillRn === selectedSkillRn);

    if (!hasSelectedSkill) {
      setSelectedSkillRn(null);
    }
  }, [selectedSkillRn, skills]);

  return (
    <>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        alignItems='stretch'
        sx={{
          width: '100%',
          height: {
            md: 'calc(99vh - 32px)',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            alignSelf: 'stretch',
            display: 'flex',
          }}
        >
          <SkillsList
            groupedSkills={groupedSkills}
            openNew={openNew}
            onSelectSkill={handleSelectSkill}
            selectedSkillRn={selectedSkillRn}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            backgroundColor: '#FFFFFF',
          }}
        >
          <SkillsView skill={selectedSkill} onDelete={openDeleteConfirm} onEdit={handleEditSkill} />
        </Box>
      </Stack>
      <SkillsNew
        active={activeNew}
        onClose={closeNew}
        handleCreateSkill={handleCreateSkill}
        groupOptions={groupOptions}
        skill={editingSkill}
      />
      {
        isShowingConfirmDeleteDialog &&
        <DeleteConfirmDialog
          setDialogOpen={setIsShowingConfirmDeleteDialog}
          onClickConfirmDelete={handleDeleteSkill}
        />
      }
    </>
  );
};

SkillsScene.propTypes = {
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  skillsAdd: PropTypes.func.isRequired,
  skillsRemove: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(SkillsScene));
